import React from 'react';
import { Moon, Sun, Monitor } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { cn } from '../lib/utils';

export function ThemeToggle() {
  const { theme, setTheme, actualTheme } = useTheme();

  const toggleTheme = () => {
    if (theme === 'light') {
      setTheme('dark');
    } else if (theme === 'dark') {
      setTheme('system');
    } else {
      setTheme('light');
    }
  };

  const getIcon = () => {
    if (theme === 'system') {
      return <Monitor className="h-4 w-4" />;
    }
    return actualTheme === 'dark' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />;
  };

  const getTooltipText = () => {
    if (theme === 'light') return 'Switch to dark mode';
    if (theme === 'dark') return 'Switch to system mode';
    return 'Switch to light mode';
  };

  return (
    <button
      onClick={toggleTheme}
      className={cn(
        'relative p-2 rounded-lg transition-all duration-200',
        'hover:bg-accent hover:text-accent-foreground',
        'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
        'group'
      )}
      aria-label={getTooltipText()}
      title={getTooltipText()}
    >
      <div className="relative">
        {getIcon()}

        {/* Subtle glow effect */}
        <div
          className={cn(
            'absolute inset-0 rounded-full transition-opacity duration-300 opacity-0',
            'group-hover:opacity-20 bg-primary blur-sm'
          )}
        />

        {/* Indicator dot for system theme */}
        {theme === 'system' && (
          <div className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-primary animate-pulse" />
        )}
      </div>

      {/* Ripple effect */}
      <div
        className={cn(
          'absolute inset-0 rounded-full transition-transform duration-200 scale-0',
          'group-active:scale-110 group-active:bg-primary/10'
        )}
      />
    </button>
  );
}

// Advanced theme selector dropdown
export function ThemeSelector() {
  const { theme, setTheme, actualTheme } = useTheme();
  const [isOpen, setIsOpen] = React.useState(false);

  const themes = [
    { value: 'light', label: 'Light', icon: Sun },
    { value: 'dark', label: 'Dark', icon: Moon },
    { value: 'system', label: 'System', icon: Monitor },
  ] as const;

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-lg',
          'bg-background border border-border',
          'hover:bg-accent hover:text-accent-foreground',
          'focus:outline-none focus:ring-2 focus:ring-ring',
          'transition-colors duration-200'
        )}
      >
        {theme === 'system' ? (
          <Monitor className="h-4 w-4" />
        ) : actualTheme === 'dark' ? (
          <Moon className="h-4 w-4" />
        ) : (
          <Sun className="h-4 w-4" />
        )}
        <span className="text-sm capitalize">{theme}</span>
      </button>

      {isOpen && (
        <div
          className={cn(
            'absolute top-full mt-1 right-0 z-50',
            'glass-card rounded-lg shadow-lg',
            'min-w-[120px] p-1',
            'animate-slide-in-from-top'
          )}
        >
          {themes.map(({ value, label, icon: Icon }) => (
            <button
              key={value}
              onClick={() => {
                setTheme(value);
                setIsOpen(false);
              }}
              className={cn(
                'w-full flex items-center gap-2 px-3 py-2 rounded-md',
                'hover:bg-accent hover:text-accent-foreground',
                'transition-colors duration-150 text-sm',
                theme === value && 'bg-primary/10 text-primary font-medium'
              )}
            >
              <Icon className="h-4 w-4" />
              {label}
              {theme === value && value === 'system' && (
                <div className="ml-auto w-2 h-2 rounded-full bg-primary animate-pulse" />
              )}
            </button>
          ))}
        </div>
      )}

      {/* Backdrop */}
      {isOpen && <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />}
    </div>
  );
}
